import { datadogLogs } from "@datadog/browser-logs";

import { env } from "~/env";

type DatadogSite =
  | "datadoghq.com"
  | "datadoghq.eu"
  | "us3.datadoghq.com"
  | "us5.datadoghq.com"
  | "ddog-gov.com";

/**
 * Initialize DataDog Logs
 * This should be called once at the application startup
 */
const initializeDatadogLogs = () => {
  if (!env.VITE_DATADOG_ENABLED) {
    return;
  }

  // Only initialize if we have the required configuration
  if (!env.VITE_DATADOG_CLIENT_TOKEN) {
    console.warn(
      "DataDog Logs not initialized: Missing VITE_DATADOG_CLIENT_TOKEN",
    );
    return;
  }

  // Don't initialize in test environment
  if (env.VITE_NODE_ENV === "test") {
    return;
  }

  datadogLogs.init({
    clientToken: env.VITE_DATADOG_CLIENT_TOKEN,
    site: env.VITE_DATADOG_SITE as DatadogSite,
    service: env.VITE_DATADOG_SERVICE_NAME,
    env: env.VITE_DATADOG_ENV,
    sessionSampleRate: env.VITE_DATADOG_SAMPLING_RATE,

    // Forward all console errors to Datadog
    forwardErrorsToLogs: true,

    // Enable verbose logging for debugging
    silentMultipleInit: false,

    // Custom configuration
    beforeSend: (_event, _context) => {
      // You can modify or filter log events before they're sent
      // For example, you might want to sanitize sensitive data
      // Return true to send the event, false to drop it
      return true;
    },
  });
};

/**
 * Add custom global context to DataDog Logs
 */
const addDatadogLogsGlobalContext = (
  key: string,
  value: string | number | boolean | object,
) => {
  if (datadogLogs.getInitConfiguration()) {
    datadogLogs.setGlobalContext({ [key]: value });
  }
};

/**
 * Add custom user context to DataDog Logs
 * Call this when user authentication state changes
 */
const setDatadogLogsUser = (user: {
  id?: string;
  name?: string;
  email?: string;
  [key: string]: string | number | boolean | undefined;
}) => {
  if (datadogLogs.getInitConfiguration()) {
    datadogLogs.setUser(user);
  }
};

/**
 * Get direct access to the DataDog logs instance for advanced usage
 */
const getDatadogLogsInstance = () => {
  return datadogLogs;
};

export {
  addDatadogLogsGlobalContext,
  getDatadogLogsInstance,
  initializeDatadogLogs,
  setDatadogLogsUser,
};
